/**
 * Dynamically matches a raw Companies House company_type code (e.g. "ltd", "plc")
 * against the list of company types fetched from the DB via /get_company_types.
 *
 * Strategy:
 *  1. If the raw value already matches a DB CompanyType exactly → return as-is.
 *  2. Extract tokens from the raw code (split on "-" and spaces) and check if any
 *     token appears inside a DB CompanyType string (case-insensitive).
 *  3. Return null if no match found — caller should leave companyType blank so the
 *     user is forced to select manually.
 *
 * @param {string} rawCode  - e.g. "ltd", "plc", "llp", "registered-overseas-entity"
 * @param {Array}  companyTypes - array from /get_company_types: [{ CompanyType, ... }]
 * @returns {string|null}  matched CompanyType string or null
 */
export const matchCompanyType = (rawCode, companyTypes) => {
  if (!rawCode || !Array.isArray(companyTypes) || companyTypes.length === 0) return null;

  const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();

  const normalizedCode = normalize(rawCode.replace(/-/g, " "));

  // 1. Exact match (handles case where admin used the same string as CH code)
  const exact = companyTypes.find(
    (ct) => normalize(ct.CompanyType) === normalizedCode
  );
  if (exact) return exact.CompanyType;

  // 2. Token match — each token from the code must appear in the DB type
  const tokens = normalizedCode.split(/\s+/).filter((t) => t.length >= 2);

  // Score each DB type by how many tokens match
  let bestMatch = null;
  let bestScore = 0;

  for (const ct of companyTypes) {
    const normalizedType = normalize(ct.CompanyType);
    const score = tokens.filter((token) => normalizedType.includes(token)).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = ct.CompanyType;
    }
  }

  // Require at least one token to match
  return bestScore > 0 ? bestMatch : null;
};
